import { useState } from 'react';
import { LayoutGrid, ArrowRightLeft, PieChart, Target, LogOut, Settings as SettingsIcon, User, Plus, Landmark, X, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Drawer, DrawerContent, DrawerTitle } from './ui/drawer';
import TransactionDialog from './TransactionDialog';
import Logo from './Logo';
import ProfilePhoto from './ProfilePhoto';
import { useProfileMenu } from '../context/ProfileMenuContext';
// Order and names follow redesign.md (Fase 3): the five `main` sections are
// the phone's bottom tabs; the `cuenta` items live in the desktop sidebar and,
// on phones, in the Perfil sheet opened from Inicio's avatar (ProfileMenu).
const navItems = [
  { id: 'dashboard', label: 'Inicio', icon: LayoutGrid, group: 'main' },
  { id: 'transactions', label: 'Movimientos', icon: ArrowRightLeft, group: 'main' },
  { id: 'chat', label: 'Coach', icon: Sparkles, group: 'main' },
  { id: 'charts', label: 'Análisis', icon: PieChart, group: 'main' },
  { id: 'goals', label: 'Metas', icon: Target, group: 'main' },
  { id: 'investments', label: 'Inversiones', icon: Landmark, group: 'cuenta' },
  { id: 'settings', label: 'Configuración', icon: SettingsIcon, group: 'cuenta' },
];
const mainItems = navItems.filter(i => i.group === 'main');
const accountItems = navItems.filter(i => i.group === 'cuenta');
// Categorías has no nav entry of its own — it's reached from Movimientos'
// filter row (and Inicio's Presupuestos), so while it's open, Movimientos is
// the item that reads as active.
const isNavActive = (item, currentPage) =>
  currentPage === item.id || (item.id === 'transactions' && currentPage === 'categories');

function SidebarItem({ item, active, onNavigate }) {
  const Icon = item.icon;
  return (
    <button
      data-testid={`nav-${item.id}`}
      onClick={() => onNavigate(item.id)}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 active:scale-[0.97] ${active
        ? 'bg-violet-500/10 text-violet-400'
        : 'text-slate-400 hover:text-white hover:bg-white/5'
        }`}
    >
      <Icon className="w-5 h-5" strokeWidth={active ? 2 : 1.5} />
      {item.label}
    </button>
  );
}

export function Sidebar({ currentPage, onNavigate, onLogout, userName, userPhoto, categories, goals, onRefresh }) {
  const [showAddTxn, setShowAddTxn] = useState(false);

  return (
    // No backdrop-blur-xl here on purpose: same fixed, always-mounted panel
    // as mobile-nav below, just for the desktop breakpoint — an Android
    // tablet in landscape can still land on this instead of mobile-nav. See
    // index.css .glass-card for why this app avoids live backdrop-filter blur.
    // Layout (display/flex-direction) comes only from .sidebar in App.css —
    // a Tailwind `flex` here would compete with its mobile `display: none`,
    // and which one wins depends on stylesheet order.
    <div className="sidebar bg-slate-900/70 border-r border-white/5 p-4" data-testid="sidebar">
      {/* Logo */}
      <div className="px-3 py-4 mb-6">
        <Logo size={36} textClassName="text-lg" />
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1">
        {mainItems.map(item => (
          <SidebarItem key={item.id} item={item} active={isNavActive(item, currentPage)} onNavigate={onNavigate} />
        ))}
        <div className="pt-4 mt-4 border-t border-white/5 space-y-1">
          {accountItems.map(item => (
            <SidebarItem key={item.id} item={item} active={isNavActive(item, currentPage)} onNavigate={onNavigate} />
          ))}
        </div>
      </nav>

      {/* Voice entry lives next to the add button on Inicio — no floating
          mic here anymore (redesign.md, Fase 3). */}
      <div className="mt-4 mb-2 px-3">
        <Button
          data-testid="add-transaction-btn"
          onClick={() => setShowAddTxn(true)}
          className="w-full bg-violet-500 hover:bg-violet-600 text-on-brand rounded-xl h-11 text-sm font-semibold transition-all duration-200 active:scale-[0.98]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Agregar
        </Button>
      </div>

      <TransactionDialog
        open={showAddTxn}
        onOpenChange={setShowAddTxn}
        categories={categories}
        goals={goals}
        onSuccess={onRefresh}
      />

      {/* User & Logout */}
      <div className="border-t border-white/5 pt-4 mt-4 space-y-2">
        {userName && (
          <div className="px-3 py-2 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full overflow-hidden bg-slate-800 flex items-center justify-center flex-shrink-0">
              <ProfilePhoto src={userPhoto} alt="Perfil" fallback={<User className="w-4 h-4 text-slate-400" />} />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-slate-200 font-medium truncate leading-tight">{userName}</p>
            </div>
          </div>
        )}
        {onLogout && (
          <button
            data-testid="logout-btn"
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:text-rose-400 hover:bg-rose-400/10 transition-all duration-200 active:scale-[0.97]"
          >
            <LogOut className="w-4.5 h-4.5" strokeWidth={1.5} />
            Cerrar sesión
          </button>
        )}
      </div>
    </div>
  );
}

// Phone bottom bar: the five main sections, icon + name (redesign.md, Fase 3).
// No backdrop-blur on purpose: this bar is fixed and mounted on every mobile
// screen, so a blur would be a live GPU compositing layer the whole time —
// the same class of black-screen crash once traced to the onboarding currency
// step and the category picker (see index.css .glass-card). Its height and
// the iPhone home-indicator inset come from .mobile-nav in App.css, which
// also pads .main-content so nothing scrolls under the bar.
export function MobileNav({ currentPage, onNavigate }) {
  return (
    <nav className="mobile-nav fixed bottom-0 inset-x-0 z-50 bg-nav border-t border-white/5" data-testid="mobile-nav" aria-label="Navegación principal">
      <div className="grid grid-cols-5 w-full h-[var(--nav-h)]">
        {mainItems.map(item => {
          const Icon = item.icon;
          const active = isNavActive(item, currentPage);
          return (
            <button
              key={item.id}
              data-testid={`mobile-nav-${item.id}`}
              onClick={() => onNavigate(item.id)}
              aria-current={active ? 'page' : undefined}
              className={`min-w-0 min-h-[44px] flex flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors duration-200 active:scale-95 ${active ? 'text-violet-500' : 'text-slate-500'}`}
            >
              <Icon className="w-[22px] h-[22px]" strokeWidth={active ? 2.2 : 1.6} />
              <span className="truncate max-w-full px-0.5">{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

// Avatar shown at the right of every main page's header on phones; opens the
// Perfil sheet below. Desktop has the same items in the sidebar, so it hides.
export function ProfileButton({ className = '' }) {
  const { openProfileMenu, photo } = useProfileMenu();
  return (
    <button
      type="button"
      onClick={openProfileMenu}
      aria-label="Perfil"
      data-testid="profile-btn"
      className={`md:hidden w-12 h-12 shrink-0 rounded-full flex items-center justify-center ${className}`}
    >
      <span className="w-10 h-10 rounded-full overflow-hidden bg-slate-800 ring-1 ring-white/10 flex items-center justify-center">
        <ProfilePhoto src={photo} alt="Perfil" fallback={<User className="w-5 h-5 text-slate-400" />} />
      </span>
    </button>
  );
}

// Perfil sheet — on phones, the way to Configuración, Inversiones and
// Cerrar sesión now that the bottom bar holds only the five main tabs.
// Rendered once by App; opened from ProfileButton in any page header.
export function ProfileMenu({ open, onOpenChange, onNavigate, onLogout, userName, userPhoto }) {
  const go = (id) => { onOpenChange(false); onNavigate(id); };
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="bg-slate-900 border-white/10 text-slate-200 pb-safe" data-testid="profile-menu">
        <DrawerTitle className="sr-only">Perfil</DrawerTitle>
        <div className="flex items-center justify-between px-5 pb-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-800 flex items-center justify-center shrink-0">
              <ProfilePhoto src={userPhoto} alt="Perfil" fallback={<User className="w-5 h-5 text-slate-400" />} />
            </div>
            <span className="text-base font-semibold text-white truncate">{userName || 'Perfil'}</span>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            aria-label="Cerrar"
            className="w-11 h-11 -mr-2 rounded-xl flex items-center justify-center text-slate-500 hover:text-white hover:bg-white/10 transition-all active:scale-90"
            data-testid="profile-menu-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-3 space-y-1">
          {accountItems.map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => go(item.id)}
                data-testid={`profile-menu-${item.id}`}
                className="w-full min-h-[48px] flex items-center gap-3 px-3 rounded-xl text-sm font-medium text-slate-200 hover:bg-white/5 transition-colors"
              >
                <Icon className="w-5 h-5 text-slate-400" strokeWidth={1.75} />
                {item.label}
              </button>
            );
          })}
        </div>
        {onLogout && (
          <div className="px-3 pt-3 pb-6 mt-3 border-t border-white/5">
            <button
              data-testid="profile-menu-logout"
              onClick={() => { onOpenChange(false); onLogout(); }}
              className="w-full min-h-[48px] flex items-center gap-3 px-3 rounded-xl text-sm font-medium text-rose-400 hover:bg-rose-400/10 transition-colors"
            >
              <LogOut className="w-5 h-5" strokeWidth={1.75} />
              Cerrar sesión
            </button>
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
}
